import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMarketDepth } from "@/hooks/use-trading";

interface OrderBookProps {
  coinId: string;
  className?: string;
}

export function OrderBook({ coinId, className }: OrderBookProps) {
  const { orderBook, currentPrice, isLoading } = useMarketDepth(coinId);

  if (isLoading) {
    return (
      <div className={cn("rounded-xl bg-card border border-border", className)}>
        <div className="p-3 border-b border-border">
          <h3 className="font-semibold text-sm text-foreground">Order Book</h3>
        </div>
        <div className="p-2 space-y-1">
          {Array.from({ length: 16 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const maxVolume = useMemo(() => {
    const allVolumes = [...orderBook.bids, ...orderBook.asks].map((o) => o.volume);
    return Math.max(...allVolumes, 1);
  }, [orderBook]);

  return (
    <div className={cn("rounded-xl bg-card", className)}>
      <div className="p-3 border-b border-border">
        <h3 className="font-semibold text-sm text-foreground">Order Book</h3>
      </div>

      <ScrollArea className="w-full rounded-xl border border-border">
        <div className="min-w-[600px]">
          <table role="table" className="w-full">
            <thead>
              <tr className="text-xs sm:text-sm text-muted-foreground">
                <th scope="col" className="text-left p-2 text-xs sm:text-sm">Price (SOL)</th>
                <th scope="col" className="text-right p-2 text-xs sm:text-sm">Amount</th>
                <th scope="col" className="text-right p-2 text-xs sm:text-sm">Total</th>
              </tr>
            </thead>
          <tbody>
            {/* Asks (Sell orders) - reversed so lowest is at bottom */}
            {orderBook.asks
              .slice()
              .reverse()
              .slice(0, 8)
              .map((order, i) => (
                <tr
                  key={`ask-${i}`}
                  className="text-xs sm:text-sm relative"
                >
                  <td
                    className="absolute inset-0 bg-destructive/10 rounded"
                    style={{ width: `${(order.volume / maxVolume) * 100}%`, right: 0, left: "auto" }}
                  />
                  <td className="p-2 font-mono text-destructive relative z-10 text-xs sm:text-sm">
                    {order.price.toFixed(8)}
                  </td>
                  <td className="p-2 font-mono text-right relative z-10 text-xs sm:text-sm">
                    {(order.volume / 1000).toFixed(1)}K
                  </td>
                  <td className="p-2 font-mono text-right text-muted-foreground relative z-10 text-xs sm:text-sm">
                    {(order.total / 1000).toFixed(2)}K
                  </td>
                </tr>
              ))}

            {/* Current Price Divider */}
            <tr>
              <td colSpan={3} className="p-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-sm font-mono font-bold text-primary px-2">
                    ${currentPrice.toFixed(8)}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </td>
            </tr>

            {/* Bids (Buy orders) */}
            {orderBook.bids.slice(0, 8).map((order, i) => (
              <tr
                key={`bid-${i}`}
                className="text-xs sm:text-sm relative"
              >
                <td
                  className="absolute inset-0 bg-success/10 rounded"
                  style={{ width: `${(order.volume / maxVolume) * 100}%`, right: 0, left: "auto" }}
                />
                <td className="p-2 font-mono text-success relative z-10 text-xs sm:text-sm">
                  {order.price.toFixed(8)}
                </td>
                <td className="p-2 font-mono text-right relative z-10 text-xs sm:text-sm">
                  {(order.volume / 1000).toFixed(1)}K
                </td>
                <td className="p-2 font-mono text-right text-muted-foreground relative z-10 text-xs sm:text-sm">
                  {(order.total / 1000).toFixed(2)}K
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </ScrollArea>
    </div>
  );
}
